// src/views/QuizView.jsx
import React, { useMemo, useEffect } from 'react';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import Mascot from '../components/Mascot';
import { useQuizViewLocal } from '../hooks/useQuizViewLocal';
import QuizProgressBar from '../components/quiz/QuizProgressBar';
import QuizFeedback from '../components/quiz/QuizFeedback';
import QuestionMC from '../components/quiz/QuestionMC';
import QuestionInput from '../components/quiz/QuestionInput';
import QuestionOrder from '../components/quiz/QuestionOrder';
import QuestionImageMC from '../components/quiz/QuestionImageMC';
import QuestionMatch from '../components/quiz/QuestionMatch';
import PassageDisplay from '../components/quiz/PassageDisplay';

export default function QuizView({
  lesson,
  qIdx,
  feedback,
  selected,
  userSentence,
  sentenceWords,
  setView,
  handleSimpleAnswer,
  handleWordClick,
  checkOrderAnswer,
  nextQuestion,
  retryQuestion,
  prevQuestion,
  mascotMessage,
  dismissMascot,
  onWrongAnswer,
  onExit,
  streak,
  lives, // ✅ Recibimos las vidas como prop para calcular si mostrar mochila
  sessionXP,
  locationTitle,
  teacherSkin,
  activeSkin,
  clearAllWords,
}) {
  const title = lesson?.title?.toLowerCase() || "";
  const isReview = title.includes('review') || lesson?.isBossMode;
  const isReading = title.includes('reading');
  const isWeeklyChallenge = Boolean(
    lesson?.weeklyChallengeId || 
    lesson?.returnView === 'weekly-challenge' || 
    lesson?.id?.startsWith('weekly')
  );

  const currentQ = lesson?.questions?.[qIdx];

  // Bloquear el scroll de fondo durante ejercicios de weekly challenge
  useEffect(() => {
    if (isWeeklyChallenge) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isWeeklyChallenge]);

  const totalQ = lesson?.questions?.length || 0;

  const theme = isReview 
    ? { bg: "bg-purple-50/80", border: "border-purple-100", label: "text-purple-400" } 
    : isReading 
    ? { bg: "bg-emerald-50/80", border: "border-emerald-100", label: "text-emerald-400" } 
    : { bg: "bg-blue-50/80", border: "border-blue-100", label: "text-blue-400" };

  // ✅ Lógica de la Mochila: Si lives > 5, mostrar avatar con mochila
  const hasExtraLives = lives > 5;
  const skinName = (activeSkin || 'bribark').replace('_', '');
  const avatarName = activeSkin || 'bribark';
  const headerAvatar = (skinName === 'bribark' && hasExtraLives)
    ? `/avatars/bribarkmochila.png`
    : `/avatars/${skinName}.png`;

  // ✅ Hook local para estados internos
  const { inputText, setInputText, tempSelected, setTempSelected, matchState, setMatchState } = useQuizViewLocal(qIdx, lesson, feedback);

  // Guard anti-crash
  if (!currentQ) return <div className="min-h-screen bg-gradient-to-b from-blue-100 via-white to-white" />;

  // ✅ Activación de Grammar Hint en respuestas incorrectas
  useEffect(() => {
    if (feedback && feedback !== 'correct') {
      if (onWrongAnswer) onWrongAnswer();
    }
  }, [feedback, onWrongAnswer]);

  // ✅ Opciones mezcladas para preguntas de opción múltiple
  const shuffledOptions = useMemo(() => {
    if (!currentQ.options) return [];
    return [...currentQ.options].sort(() => Math.random() - 0.5);
  }, [currentQ]);

  // ✅ Datos mezclados para preguntas de emparejamiento
  const matchData = useMemo(() => {
    if (currentQ?.type !== 'match' || !currentQ.pairs) return null;

    const left = currentQ.pairs.map((p, i) => ({ 
      id: `L-${i}`, 
      text: p.left, 
      col: 'left' 
    }));

    const right = currentQ.pairs.map((p, i) => ({ 
      id: `R-${i}`, 
      text: p.right, 
      col: 'right' 
    }));

    return {
      left: [...left].sort(() => Math.random() - 0.5),
      right: [...right].sort(() => Math.random() - 0.5)
    };
  }, [currentQ]);

  // ✅ Helpers para preguntas de texto
  const submitTextAnswer = () => {
    if (typeof inputText === 'object' && inputText !== null) {
      const values = Object.values(inputText);
      const hasContent = values.some(v => v && v.trim().length > 0);
      if (hasContent) {
        handleSimpleAnswer(inputText);
      }
    } else {
      const textValue = String(inputText || '').trim();
      if (textValue.length > 0) {
        handleSimpleAnswer(textValue);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !feedback) submitTextAnswer();
  };

  const handlePassageContinue = () => {
    nextQuestion();
  };

  // Contenido de la pregunta reutilizable
  const questionContent = (
    <div className="animate-in fade-in duration-300">
      {currentQ.type === 'mc' && (
        <QuestionMC 
          question={currentQ}
          options={shuffledOptions}
          selected={selected}
          feedback={feedback}
          onAnswer={handleSimpleAnswer}
          tempSelected={tempSelected}
          setTempSelected={setTempSelected}
        />
      )}

      {(currentQ.type === 'blank' || currentQ.type === 'input') && (
        <QuestionInput 
          question={currentQ}
          inputText={inputText}
          setInputText={setInputText}
          selected={selected}
          feedback={feedback}
          onSubmit={submitTextAnswer}
          onKeyDown={handleKeyDown}
        />
      )}

      {currentQ.type === 'order' && (
        <QuestionOrder 
          question={currentQ}
          userSentence={userSentence}
          sentenceWords={sentenceWords}
          feedback={feedback}
          onWordClick={handleWordClick}
          onCheck={checkOrderAnswer}
          onClearAll={clearAllWords}
        />
      )}

      {currentQ.type === 'image_mc' && (
        <QuestionImageMC 
          question={currentQ}
          options={shuffledOptions}
          selected={selected}
          feedback={feedback}
          onAnswer={handleSimpleAnswer}
        />
      )}

      {currentQ.type === 'match' && matchData && (
        <QuestionMatch 
          matchData={matchData}
          matchState={matchState}
          setMatchState={setMatchState}
          currentQ={currentQ}
          feedback={feedback}
          onComplete={handleSimpleAnswer}
        />
      )}
    </div>
  );

  // ── RENDERIZADO COMO MODAL OVERLAY PARA DESAFÍO SEMANAL ──
  if (isWeeklyChallenge) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-0 sm:p-3 animate-in fade-in duration-300 select-none"
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className="w-full max-w-lg h-full sm:h-[94vh] sm:max-h-[850px] sm:rounded-[2.5rem] bg-slate-50 flex flex-col overflow-hidden shadow-2xl relative border-0 sm:border sm:border-slate-200">
          
          {/* HEADER FIJO */}
          <header className="shrink-0 px-4 pt-3.5 pb-2.5 bg-white border-b border-slate-200/80 space-y-2">
            <div className="flex items-center gap-3">
              <button
                onClick={onExit}
                className="p-2 rounded-xl bg-slate-100 border border-slate-200 text-slate-500 hover:text-indigo-600 hover:bg-slate-200 transition-all shadow-sm active:scale-90 flex items-center justify-center shrink-0 cursor-pointer"
                aria-label="Salir del ejercicio"
              >
                <ArrowLeft size={18} strokeWidth={2.5} />
              </button>

              <div className={`flex-1 ${theme.bg} rounded-xl p-2 px-3 flex items-center justify-between border ${theme.border} min-h-[44px]`}>
                <div className="flex flex-col overflow-hidden">
                  <span className={`text-[9px] font-black ${theme.label} tracking-widest uppercase`}>
                    DESAFÍO SEMANAL
                  </span>
                  <span className="text-xs font-bold text-slate-700 truncate">
                    {locationTitle}
                  </span>
                </div>

                <div className="w-8 h-8 shrink-0 ml-1.5">
                  <img
                    src={headerAvatar}
                    alt="Mascota Header"
                    className="w-full h-full object-contain"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = '/avatars/bribark.png'; 
                    }}
                  />
                </div>
              </div>
            </div>

            {/* BARRA DE PROGRESO */}
            <QuizProgressBar 
              qIdx={qIdx}
              totalQ={totalQ}
              streak={streak || 0}
              sessionXP={sessionXP || 0}
              onPrev={prevQuestion}
              onNext={nextQuestion}
              feedback={feedback}
            />
          </header>

          {/* CUERPO SCROLLEABLE INTERNO */}
          <main className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
            {/* PASSAGE */}
            {currentQ.type === 'passage' && (
              <div className="space-y-4">
                <PassageDisplay 
                  passage={currentQ.passage}
                  title={currentQ.title || "📖 Reading"}
                />
                {!feedback && ( 
                  <button
                    onClick={handlePassageContinue}
                    className="w-full bg-indigo-600 text-white py-3.5 rounded-2xl font-black text-base shadow-lg shadow-indigo-200 border-b-4 border-indigo-900 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    CONTINUAR <ChevronRight size={20} strokeWidth={3} />
                  </button>
                )}
              </div>
            )}

            {questionContent}
          </main>

          {/* MASCOTA NOTIFICACIONES */}
          {mascotMessage && mascotMessage.text && (
            <>
              {mascotMessage.variant === 'successtip' && (
                <div className="fixed inset-0 bg-black/40 z-[109]" />
              )}
              <div
                className={`fixed top-20 right-4 z-[110] animate-in slide-in-from-right-10 fade-in duration-300 max-w-[220px]
                  ${mascotMessage.variant === 'successtip' ? 'pointer-events-auto' : 'pointer-events-none'}
                `}
              >
                <div className="scale-75 origin-top-right drop-shadow-2xl">
                  <Mascot
                    type={mascotMessage.type || `${avatarName}mochila`}
                    message={mascotMessage.text}
                  />
                </div>
                {mascotMessage.variant === 'successtip' && (
                  <button
                    onClick={dismissMascot}
                    className="w-full mt-1 bg-indigo-600 text-white text-xs font-black py-2 px-3 rounded-xl shadow-lg border-b-2 border-indigo-900 active:scale-95 transition-all"
                  >
                    ¡Entendido! ✓
                  </button>
                )}
              </div>
            </>
          )}

          {/* FEEDBACK */}
          <QuizFeedback 
            feedback={feedback}
            currentQ={currentQ}
            onContinue={feedback === 'correct' ? nextQuestion : retryQuestion}
            skin={teacherSkin} 
            activeSkin={activeSkin}
            streak={streak}    
            xpGained={10} 
          />
        </div>
      </div>
    );
  }

  // ── RENDERIZADO REGULAR PARA RUTAS ESTÁNDAR ──
  return (
    <div className="space-y-6 relative pb-32 w-full max-w-3xl mx-auto">
      {/* HEADER DE NAVEGACIÓN CON TEMAS */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onExit} className="p-3 rounded-full bg-white border-2 border-slate-100 text-slate-400 hover:text-indigo-600 transition-all shadow-sm active:scale-95 flex items-center justify-center shrink-0 cursor-pointer">
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>

        <div className={`flex-1 ${theme.bg} rounded-2xl p-2 px-4 flex items-center justify-between border-2 ${theme.border} min-h-[56px] transition-colors duration-500`}>
          <div className="flex flex-col overflow-hidden">
            <span className={`text-[10px] font-black ${theme.label} tracking-widest uppercase`}>
              UBICACIÓN
            </span>
            <span className="text-sm font-bold text-slate-700 truncate">
              {locationTitle}
            </span>
          </div>

          <div className="w-10 h-10 shrink-0 ml-2">
            <img
              src={headerAvatar}
              alt="Mascota Header"
              className="w-full h-full object-contain"
              onError={(e) => { 
                e.target.onerror = null;
                e.target.src = '/avatars/bribark.png'; 
              }}
            />
          </div>
        </div>
      </div>

      {/* BARRA DE PROGRESO */}
      <QuizProgressBar 
        qIdx={qIdx}
        totalQ={totalQ}
        streak={streak || 0}
        sessionXP={sessionXP || 0}
        onPrev={prevQuestion}
        onNext={nextQuestion}
        feedback={feedback}
      />

      {/* PASSAGE (Solo lectura) */}
      {currentQ.type === 'passage' && (
        <div className="space-y-4">
          <PassageDisplay 
            passage={currentQ.passage}
            title={currentQ.title || "📖 Reading"}
          />
          {!feedback && ( 
            <button
              onClick={handlePassageContinue}
              className="w-full bg-indigo-600 text-white py-4 rounded-3xl font-black text-xl shadow-xl shadow-indigo-200 border-b-4 border-indigo-900 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 cursor-pointer"
            >
              CONTINUAR <ChevronRight size={24} strokeWidth={3} />
            </button>
          )}
        </div>
      )}

      {/* CONTENIDO DE LA PREGUNTA */}
      {questionContent}

      {/* MASCOTA DE NOTIFICACIONES */}
      {mascotMessage && mascotMessage.text && (
        <>
          {mascotMessage.variant === 'successtip' && (
            <div className="fixed inset-0 bg-black/40 z-[109]" />
          )}

          <div
            className={`fixed top-24 right-4 z-[110] animate-in slide-in-from-right-10 fade-in duration-500 max-w-[220px]
              ${mascotMessage.variant === 'successtip' ? 'pointer-events-auto' : 'pointer-events-none'}
            `}
          >
            <div className="scale-75 origin-top-right drop-shadow-2xl">
              <Mascot
                type={mascotMessage.type || `${avatarName}mochila`}
                message={mascotMessage.text}
              />
            </div>

            {mascotMessage.variant === 'successtip' && (
              <button
                onClick={dismissMascot}
                className="w-full mt-1 bg-indigo-600 text-white text-sm font-black py-2 px-4 rounded-2xl shadow-lg border-b-4 border-indigo-900 hover:scale-[1.03] active:scale-[0.97] transition-all cursor-pointer"
              >
                ¡Entendido! ✓
              </button>
            )}
          </div>
        </>
      )}

      {/* FEEDBACK DE RESPUESTA */}
      <QuizFeedback 
        feedback={feedback}
        currentQ={currentQ}
        onContinue={feedback === 'correct' ? nextQuestion : retryQuestion}
        skin={teacherSkin} 
        activeSkin={activeSkin}
        streak={streak}    
        xpGained={10} 
      />
    </div>
  );
}
